import React, { useState } from "react";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import Tooltip from "react-bootstrap/Tooltip";

import {
  deleteWhatsAppTemplate,
  provisionWhatsAppTemplates,
  syncWhatsAppTemplates,
} from "../../whatsappApi";

/**
 * WhatsAppTemplateList — the practice's approved message templates.
 *
 * Visual conventions: /CLAUDE.md §1.6 (desktop table paired with a mobile
 * .acc-card list), §1.9 (badges), §1.4 (icon-only row actions with tooltips —
 * not a kebab, because §1.10 rules those out inside a table-responsive).
 */

const STATUS_VARIANT = {
  APPROVED: "success",
  PENDING: "secondary",
  REJECTED: "danger",
  PAUSED: "warning",
  DISABLED: "secondary",
};

const STANDARD_COUNT = 4;

function StatusBadge({ template }) {
  const badge = (
    <Badge bg={STATUS_VARIANT[template.status] || "secondary"}>
      {template.status || "UNKNOWN"}
    </Badge>
  );

  if (template.status === "REJECTED" && template.rejectedReason) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{template.rejectedReason}</Tooltip>}
      >
        {badge}
      </OverlayTrigger>
    );
  }
  return badge;
}

export default function WhatsAppTemplateList({
  practiceNumber,
  templates,
  canConfigure,
  onChanged,
  showToast,
}) {
  const [busy, setBusy] = useState(null);
  const [justProvisioned, setJustProvisioned] = useState(false);

  const approvedStandard = templates.filter(
    (t) => t.name?.startsWith("practosync_") && t.status === "APPROVED",
  ).length;

  const run = async (key, fn, successMessage) => {
    setBusy(key);
    try {
      await fn();
      if (successMessage) showToast?.("success", successMessage);
      onChanged?.();
    } catch (err) {
      showToast?.(
        "danger",
        err?.response?.data?.error || "The request could not be completed.",
      );
    } finally {
      setBusy(null);
    }
  };

  const provision = () =>
    run(
      "provision",
      async () => {
        await provisionWhatsAppTemplates(practiceNumber, ["en"]);
        setJustProvisioned(true);
      },
      "Templates submitted to Meta for approval.",
    );

  const sync = () =>
    run("sync", () => syncWhatsAppTemplates(practiceNumber));

  const remove = (name) =>
    run("delete", () => deleteWhatsAppTemplate(practiceNumber, name), "Template deleted.");

  return (
    <div className="acc-list mb-3">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <h6 className="mb-0 text-primary fw-bold">Message templates</h6>
        {canConfigure && (
          <div className="ms-auto d-flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={provision}
              disabled={busy !== null || approvedStandard >= STANDARD_COUNT}
            >
              {busy === "provision" ? (
                <Spinner size="sm" animation="border" />
              ) : (
                "Provision standard reminders"
              )}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={sync}
              disabled={busy !== null}
            >
              {busy === "sync" ? (
                <Spinner size="sm" animation="border" />
              ) : (
                <>
                  <i className="fa-solid fa-rotate me-2" />
                  Refresh status
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {justProvisioned && (
        <Alert variant="info" className="py-2 px-3">
          Templates usually take a few minutes to be approved. You can leave this
          page — the status updates automatically.
        </Alert>
      )}

      {!templates.length ? (
        <p className="mb-0">
          No templates yet. Provision the standard reminders to get started.
        </p>
      ) : (
        <>
          <div className="d-none d-lg-block">
            <Table bordered hover responsive>
              <thead className="text-center">
                <tr>
                  <th className="text-center align-middle text-primary">Template</th>
                  <th className="text-center align-middle text-primary">Language</th>
                  <th className="text-center align-middle text-primary">Status</th>
                  <th className="text-center align-middle text-primary">Message</th>
                  {canConfigure && (
                    <th className="text-center align-middle text-primary">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={`${t.name}-${t.language}`}>
                    <td>{t.name}</td>
                    <td className="text-center">{t.language}</td>
                    <td className="text-center">
                      <StatusBadge template={t} />
                    </td>
                    <td>
                      <small>{t.body}</small>
                    </td>
                    {canConfigure && (
                      <td className="text-center">
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Delete template</Tooltip>}
                        >
                          <Button
                            variant="link"
                            className="text-primary text-decoration-none p-0"
                            style={{ width: 24, height: 24 }}
                            onClick={() => remove(t.name)}
                            disabled={busy !== null}
                          >
                            <i className="fa-solid fa-trash" />
                          </Button>
                        </OverlayTrigger>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="d-lg-none">
            {templates.map((t) => (
              <div className="acc-card" key={`${t.name}-${t.language}`}>
                <div className="acc-card-top">
                  <span className="acc-card-id">{t.name}</span>
                  <StatusBadge template={t} />
                </div>
                <div className="acc-card-body">
                  <div className="acc-card-field">
                    <span className="acc-card-label">Language</span>
                    <span className="acc-card-value">{t.language}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Message</span>
                    <span className="acc-card-value">{t.body}</span>
                  </div>
                  {canConfigure && (
                    <div className="acc-card-field">
                      <span className="acc-card-label">Actions</span>
                      <span className="acc-card-value">
                        <Button
                          variant="link"
                          className="text-primary text-decoration-none p-0"
                          onClick={() => remove(t.name)}
                          disabled={busy !== null}
                        >
                          <i className="fa-solid fa-trash me-2" />
                          Delete
                        </Button>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
